// The single place this app talks to the server.
//
// The session lives in an httpOnly cookie, so there is no token to attach and
// nothing sensitive is kept in localStorage — every request just needs
// `credentials: "include"` for the browser to send the cookie.

import type {
  AllowanceList,
  AuthResponse,
  Balance,
  Cadence,
  Category,
  CategoryKind,
  CreatedTransactions,
  CustomTheme,
  CustomThemeList,
  Family,
  FamilyResponse,
  Palette,
  ParentInvite,
  RecurringAllowance,
  ServerVersion,
  Transaction,
  TransactionPage,
  TxKind,
  UnseenMoney,
  User,
} from "./types";

// In dev this is "/api" and Vite proxies to the Go server, keeping everything
// same-origin. In production it is the deployed API host, and vite.config.ts
// refuses to build without VITE_API_BASE rather than let this fall back.
//
// Every path below starts with "/", so a value pasted with a trailing slash
// would otherwise produce "https://api.example.com//auth/login".
const API_BASE = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/+$/, "");

/** An error carrying the server's status and machine-readable code. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the user needs to log in again. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string };
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      // The session cookie is httpOnly, so this flag is the whole of the auth
      // story on the client: without it the browser sends no cookie at all.
      credentials: "include",
      ...(body === undefined
        ? {}
        : {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }),
    });
  } catch {
    // fetch only rejects on a transport failure, which is worth distinguishing
    // from a server that answered with an error.
    throw new ApiError(0, "network", "Can't reach the server. Is it running?");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const raw = await response.text();
  let parsed: unknown = null;
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const err = (parsed as ErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      err?.code ?? "unknown",
      err?.message ?? `Request failed (${response.status}).`,
    );
  }

  return parsed as T;
}

// --- auth -------------------------------------------------------------------

export const api = {
  /**
   * Which build is answering, for the footer. Unauthenticated, because the
   * footer renders on the login screen.
   */
  version: () => request<ServerVersion>("GET", "/version"),

  registerFamily: (input: {
    familyName: string;
    parentName: string;
    email: string;
    password: string;
    registrationCode?: string;
    familySlug?: string;
  }) => request<AuthResponse>("POST", "/auth/register-family", input),

  login: (email: string, password: string) =>
    request<AuthResponse>("POST", "/auth/login", { email, password }),

  /** Joins an existing family as a second (or later) parent, via a
   * single-use invite code another parent generated. */
  joinFamily: (input: {
    code: string;
    parentName: string;
    email: string;
    password: string;
  }) => request<AuthResponse>("POST", "/auth/join-family", input),

  loginChild: (familySlug: string, username: string, pin: string) =>
    request<AuthResponse>("POST", "/auth/login-child", {
      familySlug,
      username,
      pin,
    }),

  logout: () => request<{ ok: boolean }>("POST", "/auth/logout"),

  me: () => request<AuthResponse>("GET", "/me"),

  // --- appearance -----------------------------------------------------------
  //
  // Every route here acts on the caller, so none of them takes an ID: a member
  // sets their own look and has no say over anybody else's.

  /**
   * Sets the caller's palette — a built-in name, `custom:<id>` for one they
   * built, or the empty string to go back to the default.
   */
  setTheme: (theme: string) => request<User>("PUT", "/me/theme", { theme }),

  listCustomThemes: () => request<CustomThemeList>("GET", "/me/themes"),

  createCustomTheme: (input: {
    name: string;
    description?: string;
    palette: Palette;
  }) => request<CustomTheme>("POST", "/me/themes", input),

  updateCustomTheme: (
    id: string,
    input: { name: string; description?: string; palette: Palette },
  ) => request<CustomTheme>("PUT", `/me/themes/${id}`, input),

  /**
   * Deletes a palette. If the caller was wearing it the server puts them back
   * on the default in the same write, so no reload can catch them pointing at
   * a theme that is gone.
   */
  deleteCustomTheme: (id: string) =>
    request<void>("DELETE", `/me/themes/${id}`),

  // --- new money ------------------------------------------------------------
  //
  // Also addressed to the caller: the watermark is a child's own experience of
  // their money, so there is no ID to pass and a parent gets a 403.

  /** Money that has arrived since this child last saw a celebration. */
  newMoney: () => request<UnseenMoney>("GET", "/me/new-money"),

  /**
   * Records that the child has seen a celebration, so it is not shown again on
   * the next login.
   *
   * `throughAt` must be the value from the `newMoney` call that was actually
   * displayed. The server never moves the watermark backwards, so a late or
   * repeated call is harmless.
   */
  markMoneySeen: (throughAt: string) =>
    request<void>("POST", "/me/new-money/seen", { throughAt }),

  // --- family and children --------------------------------------------------

  family: () => request<FamilyResponse>("GET", "/family"),

  /** Changes the family's login code. Parent-only. */
  updateFamilySlug: (familySlug: string) =>
    request<Family>("PUT", "/family/slug", { familySlug }),

  /** Generates a single-use invite code for a second parent. The code is
   * only ever returned here — there is no route to read it back later. */
  createParentInvite: () => request<ParentInvite>("POST", "/family/invites"),

  /** Removes a parent from the family. The server refuses to remove the
   * last one. */
  deleteParent: (id: string) =>
    request<void>("DELETE", `/family/parents/${id}`),

  listChildren: () => request<User[]>("GET", "/children"),

  createChild: (input: {
    displayName: string;
    username: string;
    pin: string;
    avatar?: string;
  }) => request<User>("POST", "/children", input),

  updateChild: (
    id: string,
    input: {
      displayName?: string;
      username?: string;
      pin?: string;
      avatar?: string;
    },
  ) => request<User>("PATCH", `/children/${id}`, input),

  deleteChild: (id: string) => request<void>("DELETE", `/children/${id}`),

  // --- categories -----------------------------------------------------------

  listCategories: (childId: string) =>
    request<Category[]>("GET", `/children/${childId}/categories`),

  createCategory: (
    childId: string,
    input: { name: string; kind: CategoryKind; color?: string },
  ) => request<Category>("POST", `/children/${childId}/categories`, input),

  updateCategory: (
    id: string,
    input: {
      name?: string;
      color?: string;
      sortOrder?: number;
      archived?: boolean;
    },
  ) => request<Category>("PATCH", `/categories/${id}`, input),

  // --- transactions ---------------------------------------------------------

  listTransactions: (
    childId: string,
    opts: { limit?: number; cursor?: string; categoryId?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.limit) q.set("limit", String(opts.limit));
    if (opts.cursor) q.set("cursor", opts.cursor);
    if (opts.categoryId) q.set("categoryId", opts.categoryId);
    const qs = q.toString();
    return request<TransactionPage>(
      "GET",
      `/children/${childId}/transactions${qs ? `?${qs}` : ""}`,
    );
  },

  /**
   * Records money for a child.
   *
   * `categoryId` is only for the kinds that name a bucket — a withdrawal, or an
   * adjustment correcting one. Money arriving (`deposit`, `allowance`) follows
   * the child's Spend/Save split and the server refuses a bucket sent with it,
   * so a single deposit comes back as one row or two.
   */
  createTransaction: (
    childId: string,
    input: {
      amountCents: number;
      kind: TxKind;
      categoryId?: string;
      note?: string;
      occurredAt?: string;
    },
  ) =>
    request<CreatedTransactions>(
      "POST",
      `/children/${childId}/transactions`,
      input,
    ),

  updateTransaction: (
    id: string,
    input: {
      amountCents?: number;
      kind?: TxKind;
      categoryId?: string;
      clearCategory?: boolean;
      note?: string;
      occurredAt?: string;
    },
  ) => request<Transaction>("PATCH", `/transactions/${id}`, input),

  deleteTransaction: (id: string) =>
    request<void>("DELETE", `/transactions/${id}`),

  // --- balances -------------------------------------------------------------

  balance: (childId: string) =>
    request<Balance>("GET", `/children/${childId}/balance`),

  // --- recurring allowances -------------------------------------------------

  listAllowances: (childId: string) =>
    request<AllowanceList>("GET", `/children/${childId}/allowances`),

  // A rule carries no destination: the parent sets the amount and cadence, the
  // child's savePercent decides where each payout lands.
  createAllowance: (
    childId: string,
    input: {
      amountCents: number;
      cadence: Cadence;
      note?: string;
      startAt?: string;
      anchorDay?: number;
    },
  ) =>
    request<RecurringAllowance>(
      "POST",
      `/children/${childId}/allowances`,
      input,
    ),

  updateAllowance: (
    id: string,
    input: {
      amountCents?: number;
      cadence?: Cadence;
      note?: string;
      active?: boolean;
      nextRunAt?: string;
      anchorDay?: number;
    },
  ) => request<RecurringAllowance>("PATCH", `/allowances/${id}`, input),

  deleteAllowance: (id: string) => request<void>("DELETE", `/allowances/${id}`),

  // --- the child's own money controls ---------------------------------------

  /** Sets the child's Spend/Save division for future allowance payouts. */
  setSplit: (childId: string, savePercent: number) =>
    request<User>("PUT", `/children/${childId}/split`, { savePercent }),

  /** Sets the icon a child picked for themselves. */
  setChildAvatar: (childId: string, avatar: string) =>
    request<User>("PUT", `/children/${childId}/avatar`, { avatar }),

  /**
   * Records the child spending their own money. The amount is positive; the
   * server negates it and takes it from Spend, refusing an overspend.
   */
  spend: (childId: string, input: { amountCents: number; note?: string }) =>
    request<Transaction>("POST", `/children/${childId}/spend`, input),

  /**
   * Moves money between the child's own buckets, either direction.
   *
   * The amount is always positive — direction is carried by `from`/`to`, so a
   * child never types a minus sign. The server writes both legs atomically.
   */
  transfer: (
    childId: string,
    input: {
      amountCents: number;
      from: "spend" | "save";
      to: "spend" | "save";
      note?: string;
    },
  ) =>
    request<{ from: Transaction; to: Transaction }>(
      "POST",
      `/children/${childId}/transfer`,
      input,
    ),
};
